import React from 'react';
import {
  DxList,
  DxListItem,
  DxButton,
  DxText,
  DxIcon,
  DxDropdown,
} from '@dvag/design-system-react';
import { checkIcon, MAX_FUNDS_LENGTH } from 'utils/util';
import { Fond } from '../types';
import { useTranslation } from 'react-i18next';

import './LeftFondList.css';

interface Props {
  funds: Fond[];
  selectedIndex: number;
  onSelect: (idx: number) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
}

export const LeftFondList: React.FC<Props> = ({
  funds,
  selectedIndex,
  onSelect,
  onRemove,
  onAdd,
}) => {
  const { t } = useTranslation();

  return (
    <div className="fond-list-container" data-testid="fond-list-container">
      <DxList className="fond-list-desktop" id="fond-list" data-testid="fond-list">
        {funds.map((fund, idx) => (
          <div
            key={`${fund.fondName}-${fund.id}`}
            className="fond-list-row"
            data-testid={`fond-row-${idx}`}
          >
            <DxText type="ps" className="fond-list-index" data-testid={`fond-index-${idx}`}>
              {idx + 1}.
            </DxText>

            <DxListItem
              className="fond-list-item-wrapper fond-list-item"
              key={`${fund.fondName}-${fund.isin}`}
              type="selectable"
              selectionmode="manual"
              label={fund.fondName || t('fundsModal.newFund')}
              selected={idx === selectedIndex}
              unread={idx === selectedIndex}
              onClick={() => onSelect(idx)}
              data-testid={`fond-list-item-${idx}`}
              id={`fond-list-item-${idx}`}
            >
              {idx === selectedIndex && (
                <DxButton
                  className="fond-list-remove-btn"
                  theme="destructive"
                  slot="content"
                  icon={checkIcon('minus-kreis')}
                  type="text"
                  disabled={funds.length === 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(idx);
                  }}
                  data-testid={`remove-fund-btn-${idx}`}
                  id={`remove-fund-btn-${idx}`}
                />
              )}
            </DxListItem>
          </div>
        ))}
      </DxList>

      <DxDropdown
        value={selectedIndex.toString()}
        onValueChange={(e) => onSelect(Number(e.target.value))}
        id="fond-dropdown"
        data-testid="fond-dropdown"
        clearable={false}
        size="m"
        className="fond-list-mobile"
      >
        {funds.map((f, dropdownIndex) => (
          <DxListItem
            id={`fond-dropdown-item-${dropdownIndex}`}
            data-testid={`fond-dropdown-item-${dropdownIndex}`}
            key={`${f.fondName}-${f.id}`}
            value={dropdownIndex.toString()}
            label={`${dropdownIndex + 1}. ${f.fondName ?? t('fundsModal.newFund')}`}
          />
        ))}
      </DxDropdown>

      <div className="fond-list-footer">
        <div className="fond-list-counter">
          <DxIcon
            className="fond-list-counter-icon"
            size={16}
            color="gray-83"
            icon={checkIcon('information')}
            data-testid="fond-counter-icon"
          />
          <DxText type="its" data-testid="fond-counter-text">
            {t('fundsModal.addedCounter', { current: funds.length, total: 10 })}
          </DxText>
        </div>

        <DxButton
          type="secondary-m"
          icon="plus-kreis"
          iconposition="right"
          stretch
          disabled={funds.length >= MAX_FUNDS_LENGTH}
          onClick={onAdd}
          label={t('fundsModal.addFund')}
          data-testid="add-fund-btn"
          id="add-fund-btn"
        />
      </div>
    </div>
  );
};
